import { ReviewData } from '@aidkitorg/aidkit/lib/distro/review/distroReview';
import {
  Button,
  Heading
} from '@aidkitorg/component-library';
import { useMemo } from 'react';

interface DistroReviewSummaryProps {
  reviewData: ReviewData | null;
  reviewId: string;
  isClosing: boolean;
  isPublishing: boolean;
  onCloseReviewClick: () => void;
  onUpdate?: () => void;
  surveyHashMismatch?: boolean;
}

export default function DistroReviewSummary({ 
  reviewData, 
  reviewId, 
  isClosing, 
  isPublishing, 
  onCloseReviewClick,
  onUpdate,
  surveyHashMismatch
}: DistroReviewSummaryProps) {
  const isOpen = reviewData?.status === 'review_pending' || reviewData?.status === 'approved';

  const workflows = useMemo(() =>
    reviewData?.workflowStatuses?.map((workflow) => ({
      ...workflow,
      durationInSeconds: workflow.completedAt && workflow.startedAt
        ? ((new Date(workflow.completedAt).getTime() - new Date(workflow.startedAt).getTime()) / 1000).toFixed(1)
        : null,
    })) || [],
  [reviewData]);

  return (
    <>
      <div className="flex flex-row justify-between items-start">
        <Heading level="h2" className="text-lg font-semibold">Summary</Heading>
        {isOpen && <Button
          onClick={onCloseReviewClick}
          isDisabled={isClosing || isPublishing}
          loading={isClosing}
          variant="danger"
        >
          Close Review
        </Button>
        }
      </div>
      <div className="text-sm text-gray-500">
        Created by {reviewData?.createdBy} on {reviewData?.createdAt && new Date(reviewData.createdAt).toLocaleString()}
      </div>

      {reviewData?.workflowStatuses && (
        <div className="mt-2 bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-2 max-w-4xl">
          <Heading level="h3" className="text-base font-semibold text-gray-900 mb-4">Workflow Statuses</Heading>
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left text-sm font-medium text-gray-900 py-2">Workflow</th>
                <th className="text-left text-sm font-medium text-gray-900 py-2">Status</th>
                <th className="text-left text-sm font-medium text-gray-900 py-2">Duration</th>
              </tr>
            </thead>
            <tbody>
              {workflows.map((workflow) => (
                <tr key={workflow.workflowName} className="border-b last:border-b-0">
                  <td className="py-2 text-sm font-medium text-gray-900">{workflow.workflowName}</td>
                  <td className="py-2">
                    {workflow.status === 'failed' ? (
                      <span className="text-sm text-red-500 whitespace-pre-wrap">
                        {workflow.status}{workflow.error ? `: ${workflow.error}` : ''}
                      </span>
                    ) : workflow.status === 'completed' ? (
                      <span className="text-sm text-green-600 whitespace-pre-wrap">{workflow.status}</span>
                    ) : (
                      <span className="text-sm text-gray-700 whitespace-pre-wrap">{workflow.status}</span>
                    )}
                  </td>
                  <td className="py-2 text-sm text-gray-700 whitespace-pre-wrap">
                    {workflow.durationInSeconds ? `${workflow.durationInSeconds} s` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
} 
